import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, Plus, X, CheckCircle2, PackagePlus } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ProductSearchDialog — finestra di ricerca prodotti stile gestionale ERP.
 *
 * Comportamento:
 * - Resta aperta anche dopo "Inserisci" per aggiungere più prodotti consecutivi
 * - Ricerca dinamica su codice, descrizione, barcode
 * - Doppio click su una riga = inserisci subito con quantità corrente
 * - Tastiera:
 *    ↑/↓ → naviga lista
 *    Enter → inserisce il prodotto evidenziato
 *    Tab → sposta dal campo ricerca al campo quantità
 *    Esc → chiude la finestra
 * - Toggle unità Box/Pezzi
 * - Feedback visivo "Aggiunto" su ogni inserimento, ultima riga inserita evidenziata
 * - Inserimento manuale di prodotti non a catalogo (non salvati nel catalogo)
 */
const ProductSearchDialog = ({ open, onOpenChange, products, onInsert, existingItems = [], allowDuplicates = false }) => {
  const [search, setSearch] = useState('');
  const [unit, setUnit] = useState('box'); // 'box' | 'pieces'
  const [quantity, setQuantity] = useState(1);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [lastInsertedId, setLastInsertedId] = useState(null);
  const [insertedCount, setInsertedCount] = useState(0);
  // Manuale (prodotto non a catalogo)
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ code: '', description: '', pieces_per_box: 1, price: 0, iva: 22, boxes: 1 });

  const searchRef = useRef(null);
  const qtyRef = useRef(null);
  const listRef = useRef(null);

  // Reset state ogni volta che si apre la finestra
  useEffect(() => {
    if (open) {
      setSearch('');
      setQuantity(1);
      setUnit('box');
      setHighlightIndex(0);
      setLastInsertedId(null);
      setInsertedCount(0);
      setManualOpen(false);
      setManual({ code: '', description: '', pieces_per_box: 1, price: 0, iva: 22, boxes: 1 });
      setTimeout(() => searchRef.current?.focus(), 80);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products.slice(0, 500); // quando vuoto mostra primi 500 per non saturare DOM
    // Normalizza spazi multipli/leading/trailing e divide in token: tutti devono matchare
    const tokens = q.split(/\s+/).filter(Boolean);
    const matches = products.filter(p => {
      const haystack = (
        (p.code || '') + ' ' +
        (p.description || '') + ' ' +
        (p.barcode || '')
      ).toLowerCase();
      return tokens.every(t => haystack.includes(t));
    });
    return matches; // ⚠️ niente slice: mostra TUTTI i match
  }, [search, products]);

  // Reset highlight quando cambia la lista
  useEffect(() => {
    setHighlightIndex(0);
  }, [search]);

  // Scroll automatico riga evidenziata in vista
  useEffect(() => {
    if (!listRef.current) return;
    const row = listRef.current.querySelector(`[data-row-index="${highlightIndex}"]`);
    if (row) row.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  const doInsert = (product) => {
    if (!product) return;
    const qty = parseFloat(quantity) || 0;
    if (qty <= 0) {
      toast.error('Inserisci una quantità maggiore di 0');
      qtyRef.current?.focus();
      return;
    }
    const pieces_per_box = parseFloat(product.pieces_per_box) || 1;
    const isKg = (product.unit || 'pz') === 'kg';
    let boxes;
    let exactPieces; // pezzi/kg esatti da passare al form (evita perdita di precisione)
    if (unit === 'box') {
      boxes = qty;
      exactPieces = qty * pieces_per_box;
    } else {
      // Modalità "pezzi": per prodotti a Kg accettiamo decimali, per Pezzi arrotondiamo a intero
      const wholeUnits = isKg ? qty : Math.round(qty);
      exactPieces = wholeUnits;
      boxes = pieces_per_box > 0 ? Math.round((wholeUnits / pieces_per_box) * 100) / 100 : wholeUnits;
    }
    // BLOCCO: se il prodotto è già nel DDT, non lo inserisco di nuovo.
    // Eccezione: se `allowDuplicates` è true (es. aggiunta nuovo ordine), permetto duplicati.
    const alreadyIn = (existingItems || []).some(it => it.product_id === product.id);
    if (alreadyIn && !allowDuplicates) {
      toast.error(`"${product.code}" è già nel DDT — modifica la quantità sulla riga esistente`, { duration: 2800 });
      // Mantieni focus per consentire ricerca di un altro prodotto
      setTimeout(() => {
        const el = searchRef.current;
        if (el) { el.focus(); el.select(); }
      }, 30);
      return;
    }
    onInsert(product, boxes, exactPieces);
    setLastInsertedId(product.id);
    setInsertedCount(c => c + 1);
    // Reset SOLO quantità e unità — il testo di ricerca resta visibile e SELEZIONATO
    // così la prossima lettera digitata lo sovrascrive automaticamente (zero clic).
    setQuantity(1);
    setUnit('box');
    // Auto-focus + select all → l'utente può iniziare a digitare il prossimo prodotto subito.
    setTimeout(() => {
      const el = searchRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    }, 30);
  };

  // Apre il form manuale, pre-compilando descrizione con il testo cercato
  const openManualForm = () => {
    setManual({
      code: '',
      description: search.trim(),
      pieces_per_box: 1,
      price: 0,
      iva: 22,
      boxes: 1,
    });
    setManualOpen(true);
  };

  // Inserisce un prodotto non a catalogo come riga del DDT
  const doInsertManual = () => {
    const desc = (manual.description || '').trim();
    if (!desc) {
      toast.error('La descrizione è obbligatoria');
      return;
    }
    const boxes = parseFloat(manual.boxes) || 0;
    if (boxes <= 0) {
      toast.error('Inserisci una quantità (box) maggiore di 0');
      return;
    }
    const fakeProduct = {
      id: '', // niente product_id = riga manuale, non collegata a catalogo, NO decremento stock
      code: (manual.code || '').trim() || '—',
      description: desc,
      pieces_per_box: parseInt(manual.pieces_per_box) || 1,
      price: parseFloat(manual.price) || 0,
      cost: 0,
      iva: parseFloat(manual.iva) || 22,
      stock: 0,
    };
    onInsert(fakeProduct, boxes);
    setInsertedCount(c => c + 1);
    toast.success(`"${desc}" aggiunto come prodotto non a catalogo`, { duration: 1800 });
    // Mantieni form aperto per inserire altri manuali consecutivi
    setManual({ code: '', description: '', pieces_per_box: 1, price: 0, iva: 22, boxes: 1 });
    // Riporta il focus + select sulla barra di ricerca così se chiude il form manuale
    // può subito ricominciare a digitare un prodotto dal catalogo.
    setTimeout(() => {
      const el = searchRef.current;
      if (el && !manualOpen) {
        el.focus();
        el.select();
      }
    }, 30);
  };

  const handleSearchKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      doInsert(filtered[highlightIndex]);
    } else if (e.key === 'Tab' && !e.shiftKey) {
      // Tab dal campo ricerca → qty (default behavior va bene)
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onOpenChange(false);
    }
  };

  const handleQtyKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      doInsert(filtered[highlightIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        data-testid="product-search-dialog"
        className="max-w-5xl w-[95vw] p-0 gap-0 max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
        onInteractOutside={(e) => e.preventDefault()} // non chiudere cliccando fuori
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-5 py-3 border-b border-border bg-muted/40">
          <DialogTitle className="text-base font-semibold font-['Manrope'] flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Seleziona Prodotti
              {insertedCount > 0 && (
                <span
                  data-testid="inserted-counter"
                  className="ml-2 text-xs font-normal text-green-700 bg-green-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  {insertedCount} inserit{insertedCount === 1 ? 'o' : 'i'}
                </span>
              )}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Toolbar: ricerca + unità + quantità + inserisci */}
        <div className="px-5 py-3 border-b border-border bg-background">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={searchRef}
                data-testid="dialog-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearchKey}
                placeholder="Cerca per codice, descrizione o barcode... (↑↓ naviga · Invio inserisce)"
                className="h-10 pl-9 text-sm"
              />
            </div>

            <div className="flex items-center gap-1 border-2 border-blue-200 rounded-md p-0.5 bg-blue-50/40">
              <button
                type="button"
                data-testid="unit-box-btn"
                onClick={() => setUnit('box')}
                className={`px-4 h-8 text-xs font-bold rounded-sm transition uppercase tracking-wide ${unit === 'box' ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-800' : 'text-blue-700/60 hover:text-blue-700 hover:bg-white/60'}`}
              >
                Box
              </button>
              <button
                type="button"
                data-testid="unit-pieces-btn"
                onClick={() => setUnit('pieces')}
                className={`px-4 h-8 text-xs font-bold rounded-sm transition uppercase tracking-wide ${unit === 'pieces' ? 'bg-emerald-600 text-white shadow-md ring-2 ring-emerald-800' : 'text-emerald-700/60 hover:text-emerald-700 hover:bg-white/60'}`}
              >
                {(() => {
                  const hl = filtered[highlightIndex];
                  return (hl && hl.unit === 'kg') ? 'Kg' : 'Pezzi';
                })()}
              </button>
            </div>

            <div className="flex items-center gap-1.5">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Quantità</label>
              <Input
                ref={qtyRef}
                data-testid="dialog-qty-input"
                type="number"
                min="0"
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                onKeyDown={handleQtyKey}
                className="h-10 w-24 text-center font-['JetBrains_Mono'] font-semibold"
              />
            </div>

            <Button
              type="button"
              data-testid="dialog-insert-btn"
              onClick={() => doInsert(filtered[highlightIndex])}
              className="h-10 bg-green-600 hover:bg-green-700 text-white"
            >
              <Plus className="w-4 h-4 mr-1" />
              Inserisci
            </Button>

            <Button
              type="button"
              data-testid="dialog-manual-btn"
              onClick={openManualForm}
              variant="outline"
              className="h-10 border-amber-400 text-amber-700 hover:bg-amber-50"
              title="Inserisci un prodotto NON presente nel catalogo"
            >
              <PackagePlus className="w-4 h-4 mr-1" />
              Manuale
            </Button>

            <Button
              type="button"
              data-testid="dialog-close-btn"
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="h-10"
            >
              <X className="w-4 h-4 mr-1" />
              Chiudi
            </Button>
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            💡 <strong>Doppio click</strong> su un prodotto per inserirlo subito · <strong>↑↓</strong> naviga · <strong>Invio</strong> inserisce · <strong>Esc</strong> chiude · prodotto non in catalogo? clicca <strong>Manuale</strong>
          </div>
        </div>

        {/* Pannello inserimento manuale (visibile quando l'utente lo apre) */}
        {manualOpen && (
          <div
            data-testid="manual-product-form"
            className="px-5 py-3 border-b border-amber-300 bg-amber-50"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                <PackagePlus className="w-4 h-4" />
                Inserisci prodotto non a catalogo
              </div>
              <button
                type="button"
                onClick={() => setManualOpen(false)}
                className="text-amber-900 hover:text-amber-700 text-xs"
              >
                ✕ Chiudi form manuale
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
              <div className="md:col-span-2">
                <label className="text-[9px] uppercase tracking-wider text-amber-900">Codice</label>
                <Input
                  data-testid="manual-code"
                  value={manual.code}
                  onChange={(e) => setManual({ ...manual, code: e.target.value })}
                  placeholder="—"
                  className="h-9 text-sm bg-white"
                />
              </div>
              <div className="md:col-span-4">
                <label className="text-[9px] uppercase tracking-wider text-amber-900">Descrizione *</label>
                <Input
                  data-testid="manual-description"
                  value={manual.description}
                  onChange={(e) => setManual({ ...manual, description: e.target.value })}
                  placeholder="Es. Birra speciale 33cl"
                  autoFocus
                  className="h-9 text-sm bg-white"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-[9px] uppercase tracking-wider text-amber-900">Box</label>
                <Input
                  data-testid="manual-boxes"
                  type="number" step="0.01" min="0.01"
                  value={manual.boxes}
                  onChange={(e) => setManual({ ...manual, boxes: e.target.value })}
                  className="h-9 text-center font-['JetBrains_Mono'] text-sm bg-white"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-[9px] uppercase tracking-wider text-amber-900">Pz/Box</label>
                <Input
                  data-testid="manual-pieces-per-box"
                  type="number" min="1"
                  value={manual.pieces_per_box}
                  onChange={(e) => setManual({ ...manual, pieces_per_box: e.target.value })}
                  className="h-9 text-center font-['JetBrains_Mono'] text-sm bg-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-[9px] uppercase tracking-wider text-amber-900">Prezzo (€)</label>
                <Input
                  data-testid="manual-price"
                  type="number" step="0.01"
                  value={manual.price}
                  onChange={(e) => setManual({ ...manual, price: e.target.value })}
                  className="h-9 text-right font-['JetBrains_Mono'] text-sm bg-white"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-[9px] uppercase tracking-wider text-amber-900">IVA</label>
                <Input
                  data-testid="manual-iva"
                  type="number" min="0" max="100"
                  value={manual.iva}
                  onChange={(e) => setManual({ ...manual, iva: e.target.value })}
                  className="h-9 text-center font-['JetBrains_Mono'] text-sm bg-white"
                />
              </div>
              <div className="md:col-span-1 flex items-end">
                <Button
                  type="button"
                  data-testid="manual-insert-btn"
                  onClick={doInsertManual}
                  className="h-9 w-full bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="text-[10px] text-amber-800 mt-1.5">
              ℹ️ Il prodotto verrà inserito SOLO in questa bolla, senza salvarlo nel catalogo permanente.
            </div>
          </div>
        )}

        {/* Tabella prodotti */}
        <div ref={listRef} className="flex-1 overflow-auto" data-testid="dialog-product-list">
          {filtered.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              <p className="mb-3">Nessun prodotto trovato per &quot;<strong>{search}</strong>&quot;</p>
              <Button
                type="button"
                data-testid="dialog-no-results-manual-btn"
                onClick={openManualForm}
                variant="outline"
                className="border-amber-400 text-amber-700 hover:bg-amber-50"
              >
                <PackagePlus className="w-4 h-4 mr-1.5" />
                Inserisci &quot;{search}&quot; manualmente
              </Button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/70 backdrop-blur z-10">
                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-3 py-2 font-semibold w-[110px]">Codice</th>
                  <th className="text-left px-3 py-2 font-semibold">Descrizione</th>
                  <th className="text-center px-2 py-2 font-semibold w-[70px]">Pz/Box</th>
                  <th className="text-left px-2 py-2 font-semibold w-[130px]">Barcode</th>
                  <th className="text-center px-2 py-2 font-semibold w-[55px]">IVA</th>
                  <th className="text-right px-2 py-2 font-semibold w-[90px]">Prezzo</th>
                  <th className="text-center px-2 py-2 font-semibold w-[70px]">Stock</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, idx) => {
                  const isHighlight = idx === highlightIndex;
                  const isLastInserted = p.id === lastInsertedId;
                  // Se `allowDuplicates` è true (nuovo ordine in DDT esistente), ignoriamo
                  // il flag "già nel DDT" così l'utente può inserirlo di nuovo.
                  const isAlready = !allowDuplicates && (existingItems || []).some(it => it.product_id === p.id);
                  return (
                    <tr
                      key={p.id}
                      data-row-index={idx}
                      data-testid={`dialog-product-row-${idx}`}
                      onClick={() => setHighlightIndex(idx)}
                      onDoubleClick={() => doInsert(p)}
                      className={`cursor-pointer border-b border-border/40 transition ${
                        isAlready ? 'bg-red-50/60 opacity-60' : isHighlight ? 'bg-blue-600 text-white ring-2 ring-inset ring-blue-800 font-semibold shadow-inner' : 'hover:bg-muted/40'
                      } ${isLastInserted ? 'animate-pulse-once bg-green-50' : ''}`}
                      title={isAlready ? 'Già presente nel DDT — non puoi aggiungerlo di nuovo' : ''}
                    >
                      <td className="px-3 py-2 font-['JetBrains_Mono'] text-xs font-medium">{p.code}</td>
                      <td className="px-3 py-2">
                        <span className="text-sm">{p.description}</span>
                        {isAlready && (
                          <span className="ml-2 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-sm bg-red-200 text-red-900">
                            GIÀ NEL DDT
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center font-['JetBrains_Mono'] text-xs">{p.pieces_per_box || 1}</td>
                      <td className={`px-2 py-2 font-['JetBrains_Mono'] text-[11px] ${isHighlight ? 'text-blue-100' : 'text-muted-foreground'}`}>{p.barcode || '—'}</td>
                      <td className="px-2 py-2 text-center text-xs">{p.iva || 22}%</td>
                      <td className="px-2 py-2 text-right font-['JetBrains_Mono'] text-xs font-medium">€{(p.price || 0).toFixed(2)}</td>
                      <td className="px-2 py-2 text-center font-['JetBrains_Mono'] text-xs">{(p.stock ?? 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer info */}
        <div className="px-5 py-2 border-t border-border bg-muted/40 text-[11px] text-muted-foreground flex items-center justify-between">
          <span data-testid="dialog-results-info">
            {search.trim()
              ? `${filtered.length} risultat${filtered.length === 1 ? 'o' : 'i'} su ${products.length} prodott${products.length === 1 ? 'o' : 'i'}`
              : `${products.length} prodott${products.length === 1 ? 'o' : 'i'} in catalogo${products.length > 500 ? ' (mostro primi 500, usa la ricerca)' : ''}`
            }
          </span>
          <span>La finestra resta aperta per inserimenti consecutivi.</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductSearchDialog;
